import { z } from 'zod';

export const ProfileSchema = z.enum(['personal', 'professional']);
export type Profile = z.infer<typeof ProfileSchema>;

export const DecisionSchema = z.enum(['ALLOW', 'MODIFY', 'DEFER', 'DENY', 'STEP_UP', 'HALT']);
export type Decision = z.infer<typeof DecisionSchema>;

export const RiskLevelSchema = z.enum(['low', 'medium', 'high', 'critical']);
export type RiskLevel = z.infer<typeof RiskLevelSchema>;

export const CapabilitySchema = z.enum([
  'READ_MEMORY',
  'WRITE_MEMORY',
  'PUBLISH_CONTENT',
  'SPEND_MONEY',
  'RECEIVE_MONEY',
  'SHARE_DATA',
  'CONTACT_PERSON',
  'BUY_SERVICE',
  'SELL_ASSET',
  'CREATE_MEDIA',
  'MANAGE_ASSET',
  'BOOK_SERVICE',
  'EXECUTE_PAYMENT',
  'LICENSE_KNOWLEDGE',
  'MANAGE_ATTENTION',
  'MANAGE_EMAIL',
  'MANAGE_CALENDAR',
  'MANAGE_AUTOMATION',
]);
export type Capability = z.infer<typeof CapabilitySchema>;

export const MandateSchema = z.object({
  id: z.string().min(1),
  principalId: z.string().min(1),
  agentId: z.string().min(1).default('personal-twin'),
  profile: ProfileSchema,
  capabilities: z.array(CapabilitySchema),
  maxSpendMinor: z.number().int().nonnegative(),
  maxCumulativeSpendMinor: z.number().int().nonnegative(),
  spentMinor: z.number().int().nonnegative().default(0),
  currency: z.string().length(3),
  validUntil: z.string().datetime(),
  allowedRecipients: z.array(z.string()).default([]),
  allowedChannels: z.array(z.string()).default([]),
  allowedDataScopes: z.array(z.string()).default([]),
  allowedPurposes: z.array(z.string()).default([]),
  requiresEvidence: z.boolean().default(true),
  requiresApprovalAboveMinor: z.number().int().nonnegative(),
});
export type Mandate = z.infer<typeof MandateSchema>;

export const ProposedActionSchema = z.object({
  id: z.string().min(1),
  principalId: z.string().min(1),
  profile: ProfileSchema,
  capability: CapabilitySchema,
  purpose: z.string().min(1),
  amountMinor: z.number().int().nonnegative().default(0),
  currency: z.string().length(3).default('NOK'),
  recipient: z.string().optional(),
  channel: z.string().optional(),
  risk: RiskLevelSchema.default('low'),
  reversible: z.boolean(),
  external: z.boolean().default(false),
  dataScopes: z.array(z.string()).default([]),
  evidence: z.array(z.string()).default([]),
  createdAt: z.string().datetime().optional(),
});
export type ProposedAction = z.infer<typeof ProposedActionSchema>;

export type Clearance = {
  actionId: string;
  mandateId: string;
  decision: Decision;
  reasons: string[];
  evaluatedAt: string;
};

export type ActionReceipt = {
  id: string;
  actionId: string;
  mandateId: string;
  profile: Profile;
  decision: Decision;
  reasons: string[];
  amountMinor: number;
  currency: string;
  previousHash: string;
  receiptHash: string;
  createdAt: string;
};

export type ReceiptHashMaterial = Pick<
  ActionReceipt,
  'actionId' | 'mandateId' | 'profile' | 'decision' | 'reasons' | 'amountMinor' | 'currency' | 'previousHash' | 'createdAt'
>;

const strongerDecision = (reasons: string[]): Decision => {
  if (reasons.includes('PRINCIPAL_BINDING_MISMATCH')) return 'HALT';
  return 'DENY';
};

export function evaluateAction(actionInput: ProposedAction, mandateInput: Mandate, now = new Date()): Clearance {
  const action = ProposedActionSchema.parse(actionInput);
  const mandate = MandateSchema.parse(mandateInput);
  const reasons: string[] = [];

  if (action.principalId !== mandate.principalId) reasons.push('PRINCIPAL_BINDING_MISMATCH');
  if (action.profile !== mandate.profile) reasons.push('PROFILE_BOUNDARY');
  if (!mandate.capabilities.includes(action.capability)) reasons.push('CAPABILITY_NOT_DELEGATED');
  if (new Date(mandate.validUntil) <= now) reasons.push('MANDATE_EXPIRED');
  if (action.amountMinor > mandate.maxSpendMinor) reasons.push('SINGLE_SPEND_LIMIT_EXCEEDED');
  if (mandate.spentMinor + action.amountMinor > mandate.maxCumulativeSpendMinor) reasons.push('CUMULATIVE_SPEND_LIMIT_EXCEEDED');
  if (action.amountMinor > 0 && action.currency !== mandate.currency) reasons.push('CURRENCY_MISMATCH');
  if (action.recipient && mandate.allowedRecipients.length > 0 && !mandate.allowedRecipients.includes(action.recipient)) {
    reasons.push('RECIPIENT_NOT_ALLOWED');
  }
  if (action.channel && mandate.allowedChannels.length > 0 && !mandate.allowedChannels.includes(action.channel)) {
    reasons.push('CHANNEL_NOT_ALLOWED');
  }
  if (mandate.allowedPurposes.length > 0 && !mandate.allowedPurposes.includes(action.purpose)) {
    reasons.push('PURPOSE_NOT_ALLOWED');
  }
  const blockedScopes = action.dataScopes.filter((scope) => !mandate.allowedDataScopes.includes(scope));
  if (blockedScopes.length > 0) reasons.push('DATA_SCOPE_NOT_ALLOWED');
  if (mandate.requiresEvidence && action.evidence.length === 0) reasons.push('EVIDENCE_MISSING');

  if (reasons.length > 0) {
    return {
      actionId: action.id,
      mandateId: mandate.id,
      decision: strongerDecision(reasons),
      reasons,
      evaluatedAt: now.toISOString(),
    };
  }

  const stepUpRequired =
    !action.reversible ||
    action.amountMinor > mandate.requiresApprovalAboveMinor ||
    action.risk === 'high' ||
    action.risk === 'critical' ||
    (action.external && ['PUBLISH_CONTENT', 'SHARE_DATA', 'SELL_ASSET', 'LICENSE_KNOWLEDGE'].includes(action.capability));

  if (stepUpRequired) {
    return {
      actionId: action.id,
      mandateId: mandate.id,
      decision: 'STEP_UP',
      reasons: ['HUMAN_APPROVAL_REQUIRED'],
      evaluatedAt: now.toISOString(),
    };
  }

  return {
    actionId: action.id,
    mandateId: mandate.id,
    decision: 'ALLOW',
    reasons: ['WITHIN_MANDATE'],
    evaluatedAt: now.toISOString(),
  };
}

function stableHash(input: string): string {
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeReceiptHash(receipt: ReceiptHashMaterial): string {
  const payload = [
    receipt.actionId,
    receipt.mandateId,
    receipt.profile,
    receipt.decision,
    receipt.reasons.join(','),
    receipt.amountMinor,
    receipt.currency,
    receipt.previousHash,
    receipt.createdAt,
  ].join('|');
  return stableHash(payload);
}

export function createReceipt(
  action: ProposedAction,
  clearance: Clearance,
  previousHash = 'GENESIS',
): ActionReceipt {
  const receipt: Omit<ActionReceipt, 'receiptHash'> = {
    id: `receipt-${action.id}-${clearance.evaluatedAt}`,
    actionId: action.id,
    mandateId: clearance.mandateId,
    profile: action.profile,
    decision: clearance.decision,
    reasons: clearance.reasons,
    amountMinor: action.amountMinor,
    currency: action.currency,
    previousHash,
    createdAt: clearance.evaluatedAt,
  };

  return {
    ...receipt,
    receiptHash: computeReceiptHash(receipt),
  };
}
